/**
 * AppHeader component implemented with Material UI
 * 
 * This component provides a Material UI implementation of the AppHeader component.
 */
import React from 'react';
import { styled } from '@mui/material/styles';
import { Box, Typography, Stack } from '@mui/material';
import AuthButtons from './AuthButtons';
import ThemeToggle from './ThemeToggle';
import { AppHeaderProps } from 'types/components/atoms';

/**
 * AppHeader component
 * 
 * @param props - Component props
 * @returns AppHeader component
 */
const AppHeader: React.FC<AppHeaderProps> = ({ children, ...rest }) => {
  // Navigate to Biamp website in new tab
  const logoClick = (): void => {
    window.open('https://www.biamp.com', '_blank');
  };

  // Navigate to Biamp support in new tab
  const stoneClick = (): void => {
    window.open('https://support.biamp.com', '_blank');
  };

  return (
    <Wrapper {...rest}>
      <Upper>
        <Stone onClick={stoneClick}>
          <Typography 
            sx={{ 
              fontSize: '1.5rem', 
              color: '#74777a', 
              lineHeight: '24px' 
            }}
          >
            CORNERSTONE
          </Typography>
          <Typography 
            sx={{ 
              fontSize: '1rem', 
              color: '#000', 
              lineHeight: '16px', 
              fontFamily: 'Gotham-light' 
            }}
          >
            Technical Support Knowledge Base
          </Typography>
        </Stone>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ThemeToggle size="small" />
          <AuthButtons />
          <Logo 
            onClick={logoClick} 
            src="images/Biamp_Logo.png" 
            alt="Biamp Logo" 
          />
        </Box>
      </Upper>
      <Title title={children} />
    </Wrapper>
  );
};

// Styled components using MUI styled API
const Wrapper = styled(Stack)(({ theme }) => ({
  borderBottom: '1px solid var(--app-red)',
  flexGrow: 0
}));

const Upper = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between'
});

const Stone = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  marginTop: '0.75rem',
  marginLeft: '1.25rem',
  cursor: 'pointer'
});

const Logo = styled('img')({
  height: '40px',
  marginLeft: 'auto',
  marginRight: '1.25rem',
  marginTop: '0.75rem',
  cursor: 'pointer'
});

// Title component with TypeScript props
interface TitleProps {
  title?: React.ReactNode;
}

const Title: React.FC<TitleProps> = ({ title }) => (
  <Typography 
    variant="h2" 
    sx={{ 
      textAlign: 'center',
      fontSize: '1.5rem',
      fontWeight: 'bold'
    }}
  >
    {title}
  </Typography>
);

export type { AppHeaderProps };
export default AppHeader;
